import React from 'react'
import { IconDotsVertical } from '@tabler/icons-react'
import { ReactComponent as AlertIcon } from '@assets/images/Icons/Alert.svg'
import { ReactComponent as AttentionIcon } from '@assets/images/Icons/Attention.svg'
import { ReactComponent as CheckmarkIcon } from '@assets/images/Icons/Checkmark.svg'
import { ReactComponent as CloseIcon } from '@assets/images/Icons/Close.svg'
import { ReactComponent as InfoIcon } from '@assets/images/Icons/Info.svg'
import { ContextualMenu } from '../ContextualMenu'
import type { AlertProps, AlertVariant } from './Alert.types'
import './Alert.css'

const variantIconMap: Record<AlertVariant, React.ReactNode> = {
  critical: <AlertIcon className="ds-alert__icon-graphic" aria-hidden="true" />,
  urgent: (
    <AttentionIcon
      className="ds-alert__icon-graphic ds-alert__icon-graphic--attention"
      aria-hidden="true"
    />
  ),
  info: <InfoIcon className="ds-alert__icon-graphic" aria-hidden="true" />,
  success: (
    <CheckmarkIcon className="ds-alert__icon-graphic" aria-hidden="true" />
  ),
}

const Alert: React.FC<AlertProps> = ({
  variant = 'critical',
  layout = 'board',
  title,
  timestamp,
  description,
  primaryAction,
  secondaryAction,
  showActions = true,
  showMenu = true,
  menuItems,
  onMenuClick,
  darkMode = false,
  animate = false,
  exiting = false,
  className = '',
  style,
  icon,
}) => {
  const hasActions = showActions && (primaryAction || secondaryAction)
  const hasMenuItems = Boolean(menuItems && menuItems.length > 0)
  const isToast = layout === 'toast'

  return (
    <div
      className={`ds-alert ds-alert--${layout} ds-alert--${variant}${
        darkMode ? ' ds-alert--dark' : ''
      }${animate && !exiting ? ' ds-alert--enter' : ''}${
        exiting ? ' ds-alert--exiting' : ''
      } ${className}`.trim()}
      style={style}
      role="alert"
    >
      <div className="ds-alert__content">
        <div className="ds-alert__header">
          <div className="ds-alert__icon-badge" aria-hidden="true">
            {icon ?? variantIconMap[variant]}
          </div>

          <div className="ds-alert__heading">
            <p className="ds-alert__title">{title}</p>
            {timestamp && <p className="ds-alert__timestamp">{timestamp}</p>}
          </div>

          {showMenu && isToast ? (
            <button
              type="button"
              className="ds-alert__menu-button"
              aria-label="Dismiss"
              onClick={onMenuClick}
            >
              <CloseIcon className="ds-alert__close-icon" aria-hidden="true" />
            </button>
          ) : showMenu && hasMenuItems ? (
            <ContextualMenu
              items={menuItems ?? []}
              ariaLabel="More options"
              className="ds-alert__menu"
              darkMode={darkMode}
            />
          ) : showMenu ? (
            <button
              type="button"
              className="ds-alert__menu-button"
              aria-label="More options"
              onClick={onMenuClick}
            >
              <IconDotsVertical size={16} stroke={2} />
            </button>
          ) : null}
        </div>

        <p className="ds-alert__description">{description}</p>

        {hasActions && (
          <div className="ds-alert__actions">
            {secondaryAction && (
              <button
                type="button"
                className="ds-alert__action ds-alert__action--secondary"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                type="button"
                className="ds-alert__action ds-alert__action--primary"
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Alert
